// Single source of truth for the app's session-expiry rules.
//
// sessionStart and lastActivity are stored in localStorage (not sessionStorage)
// because they must survive a fully-closed browser / a slept device — closing
// the browser is not "activity" and must not reset either timer. sessionStorage
// is cleared on tab close, which is why it can't be used here.
//
// Known tradeoff: localStorage is editable via devtools. Accepted as a low risk
// for this internal tool for now; a future hardening pass could validate session
// age server-side (e.g. via custom claims + a Cloud Function check) rather than
// trusting the client-stored timestamps alone.

const SESSION_START_KEY = 'sessionStart';
const LAST_ACTIVITY_KEY = 'lastActivity';
// Per-tab (sessionStorage, not localStorage) marker meaning "an interactive
// login is in flight in this tab". See markLoginAttempt() for why it exists.
const LOGIN_ATTEMPT_KEY = 'loginAttemptInFlight';

export const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes of inactivity
export const ABSOLUTE_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 hours from login

/** Call on a fresh, interactive login. Resets both timers to now. */
export function startSession() {
  const now = String(Date.now());
  try {
    localStorage.setItem(SESSION_START_KEY, now);
    localStorage.setItem(LAST_ACTIVITY_KEY, now);
  } catch (err) {
    console.warn('Unable to write session timestamps to localStorage:', err);
  }
}

/** Call on genuine user activity while the app is active/visible. */
export function touchActivity() {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  } catch (err) {
    console.warn('Unable to update lastActivity in localStorage:', err);
  }
}

/** Remove both timers, e.g. on logout or forced expiry. */
export function clearSession() {
  try {
    localStorage.removeItem(SESSION_START_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch (err) {
    console.warn('Unable to clear session timestamps from localStorage:', err);
  }
}

/**
 * Call immediately BEFORE the credential call (signInWithEmailAndPassword /
 * confirmationResult.confirm), not after it.
 *
 * Firebase invokes onAuthStateChanged *before* the sign-in promise resolves,
 * so App.js's expiry check runs before the login handler can reach
 * startSession(). Without this marker that check judges a brand-new session
 * against the *previous* session's leftover timers — localStorage survives a
 * closed browser, while auth uses browserSessionPersistence and does not — and
 * force-signs-out the user mid-login. Per-tab so a login in one tab can never
 * affect another tab's session state.
 */
export function markLoginAttempt() {
  try {
    sessionStorage.setItem(LOGIN_ATTEMPT_KEY, '1');
  } catch (err) {
    console.warn('Unable to mark login attempt in sessionStorage:', err);
  }
}

/** Reads and clears the marker. True = this tab just completed an interactive login. */
export function consumeLoginAttempt() {
  try {
    const value = sessionStorage.getItem(LOGIN_ATTEMPT_KEY);
    sessionStorage.removeItem(LOGIN_ATTEMPT_KEY);
    return value === '1';
  } catch (err) {
    console.warn('Unable to read login attempt marker from sessionStorage:', err);
    return false;
  }
}

/** Drops the marker without consuming its meaning, e.g. after a failed login. */
export function clearLoginAttempt() {
  try {
    sessionStorage.removeItem(LOGIN_ATTEMPT_KEY);
  } catch (err) {
    console.warn('Unable to clear login attempt marker from sessionStorage:', err);
  }
}

/**
 * Grandfathers a session that has no sessionStart yet (e.g. a user already
 * logged in when this feature shipped, or a session restored by Firebase
 * persistence before startSession() ever ran). Stamps it to now rather than
 * force-logging the user out the moment this ships.
 */
export function ensureSessionStart() {
  try {
    const now = String(Date.now());
    if (!localStorage.getItem(SESSION_START_KEY)) {
      localStorage.setItem(SESSION_START_KEY, now);
    }
    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
      localStorage.setItem(LAST_ACTIVITY_KEY, now);
    }
  } catch (err) {
    console.warn('Unable to ensure session timestamps in localStorage:', err);
  }
}

/**
 * Returns null if the session is still valid, or a reason string describing
 * why it has expired: 'expired' (absolute timeout) or 'inactivity' (idle
 * timeout). The absolute cap is checked first since it's the hard ceiling.
 */
export function checkSessionExpiry() {
  let sessionStart;
  let lastActivity;
  try {
    sessionStart = localStorage.getItem(SESSION_START_KEY);
    lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
  } catch (err) {
    console.warn('Unable to read session timestamps from localStorage:', err);
    return null;
  }

  const now = Date.now();

  if (sessionStart && now - parseInt(sessionStart, 10) > ABSOLUTE_TIMEOUT_MS) {
    return 'expired';
  }
  if (lastActivity && now - parseInt(lastActivity, 10) > IDLE_TIMEOUT_MS) {
    return 'inactivity';
  }
  return null;
}
